module OnlineDPR.Application.Client.Report.Chart

open Fable.React
open Fable.React.Props

open OnlineDPR.Application.Shared
open OnlineDPR.Application.Client.Report.Types
open OnlineDPR.Application.Client.ChartComponent
open OnlineDPR.Application.Client.ChartComponent.Props
open OnlineDPR.Application.Client.Translations
open OnlineDPR.Application.Client.Components
open OnlineDPR.Application.Shared.Translations

let private colors = [|
    "#E50069"
    "#FDC300"
    "#662483"
    "#F088B6"
    "#95C11F"
|]

let private getLabelArray (model: ReportViewModel) =
    model.Columns
    |> List.map (fun column -> column.Label)
    |> List.toArray

let private createLabeltext value context =
    sprintf "%M" value

let private getBarDataSetsFromDataRow (category: ReportCategory) index displayDatalabels isUpdating renderedCategories =
    match category.IncludeInReport && not isUpdating && not (renderedCategories |> List.contains category.Name) with
    | false -> [], index, renderedCategories
    | true -> [{ label = (match category.TranslationKey with
                         | Some key -> CurrentTranslations.Translations.[key]
                         | None -> category.Name)
                 backgroundColor = colors.[index % colors.Length]
                 borderColor = "#666666"
                 borderWidth = 0
                 borderCapStyle = ""
                 borderDash = [||]
                 fill = false
                 data = category.DataRow
                        |> List.map (fun data -> match data with
                                                 | a when a = invalidDatapoint -> None
                                                 | _ -> Some data)
                        |> List.toArray
                 datalabels = { align = "end"
                                anchor = "start"
                                color = "#000000"
                                display = displayDatalabels
                                backgroundColor = ""
                                borderRadius = 5
                                rotation = 90
                                offset = 4
                                formatter = createLabeltext
                } } ], index + 1, category.Name :: renderedCategories

let rec private getBarDataSetsFromCategory category index displayDatalabels isUpdating renderedCategories =
    let barDataSet, newIndex, newrenderedCategories = getBarDataSetsFromDataRow category index displayDatalabels isUpdating renderedCategories
    let subBarDataSet, finalIndex, finalRenderedCategories = List.fold (fun result currentCategory -> let currentBarList, currentIndex, currentRenderedCategories = result
                                                                                                      let subBarList, newIndex, newRenderedCategories = getBarDataSetsFromCategory currentCategory currentIndex displayDatalabels isUpdating currentRenderedCategories
                                                                                                      let list = List.append currentBarList subBarList
                                                                                                      list, newIndex, newRenderedCategories) ([], newIndex, newrenderedCategories) category.Subcategories
    List.concat [
        barDataSet
        subBarDataSet
    ], finalIndex, finalRenderedCategories

let private getBarDataSets model displayDatalabels isUpdating =
    let barDataSetList, _, _ = List.fold (fun result currentCategory -> let currentBarList, currentIndex, currentRenderedCategories = result
                                                                        let subBarList, newIndex, newRenderedCategories = getBarDataSetsFromCategory currentCategory currentIndex displayDatalabels isUpdating currentRenderedCategories
                                                                        (List.append currentBarList subBarList), newIndex, newRenderedCategories) ([], 0, []) model.Categories
    barDataSetList
    |> List.toArray

let private getLineDataSetsFromDataRow (category: ReportCategory) index displayDatalabels isUpdating renderedCategories : LineDataSet list * int * string list =
    match category.IncludeInReport && not isUpdating && not (renderedCategories |> List.contains category.Name) with
    | false -> [], index, renderedCategories
    | true -> [{ label = (match category.TranslationKey with
                         | Some key -> CurrentTranslations.Translations.[key]
                         | None -> category.Name)
                 backgroundColor = colors.[index % colors.Length]
                 borderColor = colors.[index % colors.Length]
                 borderWidth = 0
                 borderCapStyle = ""
                 borderDash = [||]
                 fill = false
                 data = category.DataRow
                        |> List.map (fun data -> match data with
                                                 | a when a = invalidDatapoint -> None
                                                 | _ -> Some data)
                        |> List.toArray
                 datalabels = { align = "end"
                                anchor = "end"
                                color = "#FFFFFF"
                                display = displayDatalabels
                                backgroundColor = colors.[index % colors.Length]
                                borderRadius = 5
                                rotation = 90
                                offset = 4
                                formatter = createLabeltext
                } } ], index + 1, category.Name :: renderedCategories

let rec private getLineDataSetsFromCategory (category: ReportCategory) index displayDatalabels isUpdating renderedCategories =
    let lineDataSet, newIndex, newRenderedCategories = getLineDataSetsFromDataRow category index displayDatalabels isUpdating renderedCategories
    let subLineDataSet, finalIndex, finalRenderedCategories = List.fold (fun result currentCategory -> let currentLineList, currentIndex, currentRenderedCategories = result
                                                                                                       let subLineList, newIndex, newRenderedCategories = getLineDataSetsFromCategory currentCategory currentIndex displayDatalabels isUpdating currentRenderedCategories
                                                                                                       let list = List.append currentLineList subLineList
                                                                                                       list, newIndex, newRenderedCategories) ([], newIndex, newRenderedCategories) category.Subcategories
    List.concat [
        lineDataSet
        subLineDataSet
    ], finalIndex, finalRenderedCategories

let private getLineDataSets (model: ReportViewModel) displayDatalabels isUpdating =
    let lineDataSetList, _, _ = List.fold (fun result currentCategory -> let currentLineList, currentIndex, currentRenderedCategories = result
                                                                         let subLineList, newIndex, newRenderedCategories = getLineDataSetsFromCategory currentCategory currentIndex displayDatalabels isUpdating currentRenderedCategories
                                                                         (List.append currentLineList subLineList), newIndex, newRenderedCategories) ([], 0, []) model.Categories
    lineDataSetList
    |> List.toArray

let renderChartOptions model dispatch =
    form [ ClassName "form-inline" ]
         [ div [ ClassName "form-group" ]
               [ label [ ClassName "form-label" ]
                       [ str CurrentTranslations.Translations.[TranslationKey.DprChart_Type_Title] ]
                 select [ ClassName "form-control"
                          OnChange (fun event -> dispatch (UpdateChartOptions {model.ChartOptions with ReportChartType = (match event.Value with
                                                                                                                         | "1" -> ReportChartType.Bar
                                                                                                                         | _ -> ReportChartType.Line ) } ) ) ]
                         [ option [ Value "1" ] [ str CurrentTranslations.Translations.[TranslationKey.DprChart_Type_Bar] ]
                           option [ Value "2" ] [ str CurrentTranslations.Translations.[TranslationKey.DprChart_Type_Line] ] ] ]
           div [ ClassName "form-group" ]
               [ label [ ClassName "form-label" ]
                       [ str CurrentTranslations.Translations.[TranslationKey.DPRChart_DisplayDatalabels] ] ]
           slider model.ChartOptions.DisplayDatalabels false (Some (fun _ -> dispatch (UpdateChartOptions { model.ChartOptions with DisplayDatalabels = (not model.ChartOptions.DisplayDatalabels) } ))) None None ]

let renderChartData model =
    div [] [ (match model.ChartOptions.ReportChartType with
              | ReportChartType.Bar -> bar [ BarChartProperties.Options { responsive = true
                                                                          maintainAspectRatio = true
                                                                          scales = { yAxes = [| { ticks  = { beginAtZero = true } } |] }
                                                                          legend = { display = true
                                                                                     position = "top" } }
                                             BarChartProperties.Height(50.0)
                                             BarChartProperties.Data({ labels = getLabelArray model.Data
                                                                       datasets = getBarDataSets model.Data model.ChartOptions.DisplayDatalabels model.Updating }) ]
              | _ -> line [ LineChartProperties.Options { responsive = true
                                                          maintainAspectRatio = true
                                                          scales = { yAxes = [| { ticks  = { beginAtZero = true } } |] }
                                                          legend = { display = true
                                                                     position = "top" } }
                            LineChartProperties.Height(50.0)
                            LineChartProperties.Data({ labels = getLabelArray model.Data
                                                       datasets = (getLineDataSets model.Data model.ChartOptions.DisplayDatalabels model.Updating) } ) ] ) [] ]

let renderChart model dispatch =
    let title = str CurrentTranslations.Translations.[TranslationKey.DprChart_Title]
    let content = [ renderChartOptions model dispatch
                    renderChartData model ]
                  |> ofList
    toggleCard title content model.ShowChart (fun _ -> dispatch ToggleShowChart)